import { useMemo } from 'react';
// keymap + EditorView are re-exported by @uiw/react-codemirror, so we avoid a
// direct dependency on @codemirror/view (only pulled in transitively).
import CodeMirror, { EditorView, keymap } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { indentWithTab } from '@codemirror/commands';
import { oneDark } from '@codemirror/theme-one-dark';
import { useTheme } from '@vnetwork/ui/provider';

interface JsonEditorProps {
  value: string;
  onChange?: (v: string) => void;
  readOnly?: boolean;
  minHeight?: string;
  /** Cap the visible height and scroll past it — turns the editor into a
   *  fixed viewer (e.g. a log/terminal panel). Number = px. */
  maxHeight?: number | string;
  /** Soft-wrap long lines instead of scrolling horizontally. */
  wrap?: boolean;
  /** Grammar: `json` (syntax highlight, default) or `text` (plain — logs, raw
   *  output). Plain mode drops the JSON grammar so text isn't mis-coloured. */
  language?: 'json' | 'text';
  ariaLabel?: string;
  invalid?: boolean;
}

/**
 * CodeMirror-backed code viewer/editor. JSON mode (default) powers the S3 policy
 * screens — line numbers, syntax highlighting, real indentation. `language="text"`
 * gives a plain viewer (cluster logs, raw output) reusing the same chrome. Theme
 * follows the app's active @vnetwork/ui theme so it never renders a light surface
 * inside a dark shell (or vice-versa).
 */
export function JsonEditor({
  value,
  onChange,
  readOnly = false,
  minHeight = '320px',
  maxHeight,
  wrap = false,
  language = 'json',
  ariaLabel,
  invalid = false,
}: JsonEditorProps) {
  const [theme] = useTheme();
  const isDark = theme === 'dark';

  // Rebuild extensions only when derived inputs change — CodeMirror re-diffs
  // cheaply but the grammar/sizing theme are derived, so memoize to avoid churn.
  const extensions = useMemo(() => {
    // basicSetup ships line numbers etc; add the JSON grammar only in json mode,
    // and `indentWithTab` so Tab indents inside the editor instead of tabbing out.
    const ext = [keymap.of([indentWithTab])];
    if (language === 'json') ext.unshift(json());
    if (wrap) ext.push(EditorView.lineWrapping);
    const sizing = EditorView.theme({
      '&': { minHeight, fontSize: 'var(--vn-font-size-sm)' },
      '.cm-scroller': {
        fontFamily: 'var(--vn-font-mono)',
        ...(maxHeight != null ? { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight, overflow: 'auto' } : {}),
      },
      '.cm-content': { minHeight },
    });
    ext.push(sizing);
    if (ariaLabel) ext.push(EditorView.contentAttributes.of({ 'aria-label': ariaLabel }));
    return ext;
  }, [minHeight, maxHeight, wrap, language, ariaLabel]);

  return (
    <div
      // Chrome (border/radius) uses --vn-* tokens so it matches the Textarea it
      // replaces and stays theme-aware; `invalid` mirrors Textarea's error border.
      style={{
        border: `1px solid ${invalid ? 'var(--vn-danger-solid)' : 'var(--vn-border-strong)'}`,
        borderRadius: 'var(--vn-radius-md)',
        overflow: 'hidden',
      }}
    >
      <CodeMirror
        value={value}
        theme={isDark ? oneDark : 'light'}
        editable={!readOnly}
        readOnly={readOnly}
        extensions={extensions}
        onChange={onChange}
        basicSetup={{ lineNumbers: true, foldGutter: false }}
      />
    </div>
  );
}
